import * as React from 'react'
import { animated, useSpring } from 'react-spring'
import cx from 'classnames'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import { createSpringLerp, clamp } from 'utils/math-util'
import {
  AnimationProgressProvider,
  useAnimationProgress,
} from 'contexts/AnimationProgressContext'
import AvatarProgress from 'components/AvatarProgress'
import Avatar, { AvatarProps } from 'components/Avatar'
import styles from './SideSheetHeader.sass'
import {
  useFallbackNumberSpring,
  ScrollOffsetInterpolation,
} from 'hooks/spring-hooks'
import ScrollShadow from '../ScrollShadow'

/**
 * How much to move the header up.
 */
const DELTA = 150

interface SideSheetHeaderProps {
  children?: React.ReactNode
  scrollOffset?: ScrollOffsetInterpolation
  onCloseClick?: () => unknown
  bordered?: boolean
  /**
   * Triggers whenever the stickiness changes.
   */
  onStickyChange?: (isSticky: boolean) => unknown
}

export function SideSheetHeader({
  onCloseClick,
  children,
  onStickyChange,
  bordered,
  scrollOffset,
}: SideSheetHeaderProps) {
  const [isSticky, setIsSticky] = React.useState(false)
  scrollOffset = useFallbackNumberSpring(scrollOffset)

  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const compactionAnimationProgress = scrollOffset.to((v) => clamp(v / DELTA))

  const lerp = createSpringLerp(compactionAnimationProgress)
  const translateY = scrollOffset.to((v) => -Math.min(v, DELTA))
  useSpring({
    compactionAnimationProgress,
    immediate: true,
    onChange(r) {
      setIsSticky(() => r.value.compactionAnimationProgress === 1)
    },
  })

  React.useEffect(() => {
    onStickyChange?.(isSticky)
  }, [onStickyChange, isSticky])
  const rootStyle = {
    transform: translateY.to((v) => `translateY(${v}px)`),
  }
  const closeButtonStyle = {
    transform: lerp(0, DELTA).to((v) => `translateY(${v}px)`),
  }

  return (
    <AnimationProgressProvider progress={compactionAnimationProgress}>
      <animated.div
        className={cx(styles.root, bordered && styles.bordered)}
        ref={rootRef}
        style={rootStyle}
      >
        {children}
        {onCloseClick && (
          <animated.div className={styles.top} style={closeButtonStyle}>
            <IconButton onClick={() => onCloseClick()}>
              <Icon name="back" />
            </IconButton>
          </animated.div>
        )}
        <ScrollShadow
          borderless
          // Don't start displaying the shadow until the header sticks.
          opacity={scrollOffset.to((v) => clamp(v / DELTA - 1))}
        />
      </animated.div>
    </AnimationProgressProvider>
  )
}

interface SideSheetHeaderContentProps {
  children?: React.ReactNode
}

export function SideSheetHeaderContent({
  children,
}: SideSheetHeaderContentProps) {
  return <div className={styles.headerContent}>{children}</div>
}

interface SideSheetHeaderTextProps {
  children?: React.ReactNode
}

export function SideSheetHeaderText({ children }: SideSheetHeaderTextProps) {
  const compactionAnimationProgress = useAnimationProgress()
  const tween = createSpringLerp(compactionAnimationProgress)
  const headerTextStyle = {
    // This will move the text up without changing its' computed height.
    transform: tween(0, 12).to((v) => `translateY(${v}px)`),
  }

  return (
    <animated.div className={styles.headerText} style={headerTextStyle}>
      {children}
    </animated.div>
  )
}

interface SideSheetHeaderAvatarProps {
  /**
   * When `null`, the avatar will be rendered a bit smaller
   * so the progress around it will take up the required space,
   * even if the progress radial isn't being rendered at that time.
   *
   * When `undefined`, renders the avatar at full size.
   */
  progress?: number | null
  /**
   * Avatar subject
   */
  subject: AvatarProps['subject']
  /**
   * If specified, renders this avatar in the bottom right corner.
   * Don't use this when using progress.
   */
  cornerSubject?: AvatarProps['subject']
}

export function SideSheetHeaderAvatar({
  progress,
  subject,
  cornerSubject,
}: SideSheetHeaderAvatarProps) {
  const compactionAnimationProgress = useAnimationProgress()
  const tween = createSpringLerp(compactionAnimationProgress)
  const avatarContainerStyle = {
    opacity: tween(1, 0),
  }
  const avatar = (
    <Avatar size={progress === undefined ? 100 : 80} subject={subject} />
  )
  return (
    <animated.div
      className={cx(
        styles.avatarContainer,
        progress !== undefined && styles.hasProgress,
      )}
      style={avatarContainerStyle}
    >
      <div className={styles.avatarContainerItem}>
        {progress === undefined ? (
          avatar
        ) : (
          <AvatarProgress size={100} thickness={3} progress={progress}>
            {avatar}
          </AvatarProgress>
        )}
        {cornerSubject && (
          <div className={styles.cornerAvatar}>
            <Avatar size={40} subject={cornerSubject} />
          </div>
        )}
      </div>
    </animated.div>
  )
}

interface SideSheetHeaderTitleProps {
  children?: React.ReactNode
  isButton?: boolean
  icon?: React.ReactNode
  title?: string
  onClick?: () => unknown
}

export const SideSheetHeaderTitle = React.forwardRef(
  function SideSheetHeaderTitle(
    { isButton, onClick, children, icon, title }: SideSheetHeaderTitleProps,
    ref: React.Ref<HTMLElement>,
  ) {
    const Elem = isButton ? 'a' : 'h3'
    return (
      <Elem
        title={title}
        ref={ref as any}
        onClick={onClick}
        className={isButton ? styles.titleButton : styles.title}
      >
        <span className={styles.titleText}>{children}</span>
        {icon && <span className={styles.titleButtonIcon}>{icon}</span>}
      </Elem>
    )
  },
)

interface SideSheetHeaderDescriptionProps {
  children?: React.ReactNode
}

export function SideSheetHeaderDescription({
  children,
}: SideSheetHeaderDescriptionProps) {
  return <p className={styles.description}>{children}</p>
}
